import { useState, useEffect, useRef, useCallback } from "react";
import { trackEvent, setExperimentArm } from "../utils/telemetry";
import { loadEngine, getLoadedEngine, getEngineName, resolveEngineName } from "./faceEngines";
import { measureSharpness } from "../utils/imageQuality";

const THROTTLE_MS = 150;
// Stability hold before auto-capture. The Aug-Sep 2026 optimization A/B
// tried 1000ms (and 1400ms on iOS) against the original 1800ms: the
// shorter holds bought little once preload + warm-up landed, and cost
// liveness passes on iOS, so the hold stays at 1800ms for everyone.
// Exported so the capture UI can run its hold-progress ring on the
// exact same clock.
export const AUTO_CAPTURE_DELAY_MS = 1800;
/**
 * Model lifecycle. Both engines expose load / warmUp / detect /
 * getDiagnostics, and the active one is pinned per page load in
 * ./faceEngines, so everything below this line is engine-agnostic.
 */
let _warmupPromise = null;

// Full readiness: weights downloaded AND the engine warmed. `models_ready`
// fires here so its timing keeps meaning "a live detection would be fast".
export function ensureFaceDetectionWarmUp() {
  if (_warmupPromise) return _warmupPromise;
  let engine;
  let engineName;
  _warmupPromise = loadEngine()
    .then((mod) => {
      engine = mod;
      // Read the arm only after loadEngine pinned it behind the flags.
      engineName = getEngineName();
      // Stamp the arm on every subsequent event so the funnel can be
      // split without joining back to models_ready.
      setExperimentArm(engineName);
      return engine.load();
    })
    .then(() => engine.warmUp())
    .then(() => {
      trackEvent(
        "models_ready",
        { engine: engineName, ...engine.getDiagnostics() },
        { once: true }
      );
    })
    .catch((err) => {
      _warmupPromise = null;
      trackEvent("model_load_error", { engine: engineName || getEngineName(), error: String(err) });
      throw err;
    });
  return _warmupPromise;
}

export function preloadFaceDetectionModels({ warmUp = true } = {}) {
  // warmUp:false = download-only, used behind interactive screens like
  // the consent prompt; App.js triggers the warm-up once that screen is
  // dismissed. models_ready is owned by ensureFaceDetectionWarmUp.
  //
  // The arm is not read here: this is called from a capture screen's
  // mount effect, which can run before the flags exist. resolveEngineName
  // waits (bounded) for them, then everything downstream is stamped.
  const started = resolveEngineName().then((engineName) => {
    setExperimentArm(engineName);
    trackEvent("model_load_started", { engine: engineName }, { once: true });
    console.log("[FaceDetect] preload — engine:", engineName, "warmUp:", warmUp);
  });
  if (warmUp) return started.then(() => ensureFaceDetectionWarmUp());
  return started.then(() => loadEngine()).then((engine) => engine.load());
}

const FACE_SIZE_MIN = 0.18;
const FACE_SIZE_MAX = 0.85;
const CENTER_THRESHOLD = 0.18;
const YAW_LIMIT = 25;
const PITCH_LIMIT = 30;
const ROLL_LIMIT = 25;
// Face-region brightness floor. Was 10, which only asserts "there is a
// face here". Capture-frame telemetry (2026-09-16, face-api arm) puts
// liveness failure at 25-40% for faces with mean luma under 80 and
// 0-4% for 80-100, so the floor now sits where ROC can actually read
// the face. Applies to both engines.
// ROLLED BACK to 10 on 2026-09-16, ~70 minutes after shipping 80. Android
// capture completion within 4 minutes of the tap fell 83% -> 61% while
// iOS was unchanged. Low-end Android sensors flicker around the floor
// frame to frame, and the hold needs 1.8s of CONSECUTIVE passing frames,
// so a camera hovering near 80 never completes it. The single-frame
// capture data that justified 80 could not show that. LUMA_TARGET keeps
// the intended floor for telemetry only.
const LUMA_MEAN_MIN = 10;
const LUMA_TARGET = 80;
const LUMA_MEAN_MAX = 250;
const LUMA_STD_MIN = 5;
const DARK_ROOM_THRESHOLD = 30;
// Minimum frame sharpness (see utils/imageQuality) before the hold
// timer may run. Not a gate the user sees: the drawer checklist stays at
// 5/5 and the feedback line already says "Hold still", which is the
// right instruction for motion blur. Blur < 300 carried a 19-45%
// liveness-fail rate; > 800 carried 1.4-5.8%.
// ROLLED BACK to 0 (disabled) on 2026-09-16 for the same reason as the
// luma floor above. SHARPNESS_TARGET keeps the intended floor so
// blur_holds still measures how many otherwise-ready frames it WOULD
// have held; that number decides the next design (a capture-time retake
// rather than a continuous hold, which cannot flicker).
const SHARPNESS_MIN = 0;
const SHARPNESS_TARGET = 300;

const defaultGatingState = {
  facePresent: false,
  faceSize: false,
  centering: false,
  pose: false,
  lighting: false,
};

// Pose estimation lives in each engine: the two models expose different
// landmark sets, so each maps its own points onto the same three angles
// and the gate thresholds below apply unchanged to both.

// Reusable analysis canvases — the detection loop calls these every
// ~150ms, and allocating a fresh canvas per call churns GC and (on
// some GPUs) forces a fresh GPU→CPU readback path each time.
// `willReadFrequently` keeps the canvas on a CPU-backed surface so
// the getImageData calls don't stall on GPU readback.
let _lightingCanvas = null;
let _lightingCtx = null;
let _brightnessCanvas = null;
let _brightnessCtx = null;

// Cap for the downsampled face-box sample. Mean luma is unchanged by
// downsampling; std-dev drops slightly (high-frequency detail is
// averaged away) but face-region contrast is dominated by large-scale
// features (eyes, brows, shadows) so it stays far above the
// LUMA_STD_MIN=5 gate. Sampling ~4k pixels instead of a potentially
// 500k+ native-resolution crop removes the biggest per-frame CPU cost
// in the loop after inference itself.
const LIGHTING_SAMPLE_MAX = 64;

function analyzeLighting(video, box) {
  const x = Math.max(0, Math.round(box.x));
  const y = Math.max(0, Math.round(box.y));
  const w = Math.min(Math.round(box.width), video.videoWidth - x);
  const h = Math.min(Math.round(box.height), video.videoHeight - y);
  if (w <= 0 || h <= 0) return { mean: 0, stdDev: 0 };

  if (!_lightingCanvas) {
    _lightingCanvas = document.createElement("canvas");
    _lightingCtx = _lightingCanvas.getContext("2d", { willReadFrequently: true });
  }
  const scale = Math.min(1, LIGHTING_SAMPLE_MAX / Math.max(w, h));
  const dw = Math.max(1, Math.round(w * scale));
  const dh = Math.max(1, Math.round(h * scale));
  if (_lightingCanvas.width !== dw) _lightingCanvas.width = dw;
  if (_lightingCanvas.height !== dh) _lightingCanvas.height = dh;
  _lightingCtx.drawImage(video, x, y, w, h, 0, 0, dw, dh);
  const data = _lightingCtx.getImageData(0, 0, dw, dh).data;

  // Single pass: mean + std-dev via sum / sum-of-squares, no
  // per-pixel array allocation.
  let sum = 0;
  let sumSq = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    sum += luma;
    sumSq += luma * luma;
    count++;
  }

  const mean = count > 0 ? sum / count : 0;
  const variance = count > 0 ? Math.max(0, sumSq / count - mean * mean) : 0;
  const stdDev = Math.sqrt(variance);

  return { mean, stdDev };
}

function analyzeFrameBrightness(video) {
  const sw = 64;
  const sh = 48;
  if (!_brightnessCanvas) {
    _brightnessCanvas = document.createElement("canvas");
    _brightnessCanvas.width = sw;
    _brightnessCanvas.height = sh;
    _brightnessCtx = _brightnessCanvas.getContext("2d", { willReadFrequently: true });
  }
  _brightnessCtx.drawImage(video, 0, 0, sw, sh);
  const data = _brightnessCtx.getImageData(0, 0, sw, sh).data;
  let sum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    count++;
  }
  return count > 0 ? sum / count : 0;
}

function getFeedbackKey(gating, isTooDark) {
  if (!gating.facePresent) {
    return isTooDark ? "tooDark" : "positionFace";
  }
  if (!gating.faceSize) return "adjustDistance";
  if (!gating.centering) return "centerFace";
  if (!gating.pose) return "lookStraight";
  if (!gating.lighting) {
    return isTooDark ? "tooDark" : "improveLighting";
  }
  return "holdStill";
}

export default function useFaceDetection(videoRef, active) {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [detectionStalled, setDetectionStalled] = useState(false);
  const [modelLoadError, setModelLoadError] = useState(null);
  const [gatingState, setGatingState] = useState(defaultGatingState);
  const [feedbackKey, setFeedbackKey] = useState("positionFace");
  const [isAllPassing, setIsAllPassing] = useState(false);
  const [isTooDark, setIsTooDark] = useState(false);

  const allPassingStartRef = useRef(null);
  const rafRef = useRef(null);
  const lastDetectRef = useRef(0);
  const onAutoCaptureRef = useRef(null);
  const frameChainRef = useRef([]);
  // The most recent analysed frame, in the units the gates use. Read by
  // capture.js at shutter time so face_captured can carry the pose, size,
  // lighting and inference latency the capture decision was made on.
  // Both engines feed this identically, so an arm-to-arm read of liveness
  // failures can be split on exactly the axes ROC is sensitive to.
  const lastFrameRef = useRef(null);
  const holdSamplesRef = useRef(0);
  const blurHoldsRef = useRef(0);
  // Best frame seen during the current hold window (see FACE_BEST_FRAME).
  // One reusable full-resolution canvas; the video is copied into it only
  // when a frame beats the current best, so the common cost is the score
  // (already computed for telemetry), not a copy. Reset whenever the
  // hold resets, so it can never hand back a frame from before the user
  // last moved.
  const bestFrameRef = useRef({ canvas: null, score: -1, sharpness: null, luma: null, at: 0, considered: 0 });

  // Orientation telemetry — records device orientation during detection loop.
  // Real phones produce varying readings; scripted attacks produce none.
  const orientationRef = useRef([]);
  const orientationStartRef = useRef(null);

  // Load models. Uses the shared preloader so if VeratadFaceCapture
  // already fired this on intro-screen mount, capture.js mounts to
  // already-loaded models and skips the warm-up wait entirely.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        console.log("[FaceDetect] Ensuring models, engine:", getEngineName());
        await preloadFaceDetectionModels();
        console.log("[FaceDetect] Models ready");
        if (!cancelled) setModelsLoaded(true);
      } catch (err) {
        console.error("[FaceDetect] Failed to load models:", err);
        if (!cancelled) setModelLoadError(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOnAutoCapture = useCallback((fn) => {
    onAutoCaptureRef.current = fn;
  }, []);

  const getFrameChain = useCallback(() => [...frameChainRef.current], []);
  const resetBestFrame = () => {
    const b = bestFrameRef.current;
    b.score = -1;
    b.sharpness = null;
    b.luma = null;
    b.at = 0;
    b.considered = 0;
  };
  // Score a hold-window frame. Sharpness dominates; a face outside the
  // luma band ROC reads well (80-150 from the capture-frame curve) is
  // discounted rather than excluded, so a dim-but-sharp frame still beats
  // a bright-but-smeared one and a window with no ideal frame still
  // yields its best candidate.
  const scoreFrame = (sharpness, luma) => {
    if (sharpness == null) return -1;
    const lumaOk = luma != null && luma >= LUMA_TARGET && luma <= 150;
    return lumaOk ? sharpness : sharpness * 0.5;
  };
  const getBestFrame = useCallback(() => {
    const b = bestFrameRef.current;
    if (!b.canvas || b.score < 0) return null;
    return {
      canvas: b.canvas,
      sharpness: b.sharpness,
      luma: b.luma,
      age_ms: Math.round(performance.now() - b.at),
      considered: b.considered,
    };
  }, []);
  const getLastFrameMetrics = useCallback(
    () => (lastFrameRef.current ? { ...lastFrameRef.current, hold_samples: holdSamplesRef.current } : null),
    []
  );

  // Detection loop
  useEffect(() => {
    if (!modelsLoaded || !active) {
      console.log("[FaceDetect] Loop not starting — modelsLoaded:", modelsLoaded, "active:", active);
      return;
    }

    const video = videoRef?.current?.video || videoRef?.current;
    if (!video) {
      console.log("[FaceDetect] No video element found. videoRef.current:", videoRef?.current);
      return;
    }

    // Resolved synchronously, and before anything below registers a
    // timer or listener that would need unwinding: this effect is gated
    // on modelsLoaded, which is only set once loadEngine() has settled.
    const engine = getLoadedEngine();
    if (!engine) {
      console.warn("[FaceDetect] Loop not starting — engine module not resolved");
      return;
    }

    console.log("[FaceDetect] Detection loop starting");
    trackEvent("detection_loop_started", {}, { once: true });

    // Watchdog: a slice of Android sessions (12% of optimized-arm cameras
    // vs 2% legacy in prod telemetry) start this loop but never complete
    // inference #1 — the pre-warmed WebGL state can be dead by the time
    // the camera starts, and every detect call then fails or hangs while
    // the loop spins. After 6s without a completed inference, surface the
    // manual capture path instead of leaving the user in front of a
    // camera that never reacts. Recovers on its own if a late inference
    // eventually lands.
    let firstInferenceDone = false;
    const stallTimer = setTimeout(() => {
      if (!running || firstInferenceDone) return;
      console.warn("[FaceDetect] No inference completed 6s after loop start — enabling manual capture");
      trackEvent("first_detection_timeout", {}, { once: true });
      setDetectionStalled(true);
    }, 6000);
    frameChainRef.current = [];
    orientationRef.current = [];
    orientationStartRef.current = performance.now();
    let running = true;
    let loopCount = 0;

    // Orientation telemetry — collect device orientation samples during detection
    const orientationHandler = (e) => {
      if (orientationRef.current.length >= 30) return; // cap samples
      orientationRef.current.push({
        t: Math.round(performance.now() - (orientationStartRef.current || 0)),
        a: Math.round((e.alpha || 0) * 10) / 10,
        b: Math.round((e.beta || 0) * 10) / 10,
        g: Math.round((e.gamma || 0) * 10) / 10,
      });
    };
    window.addEventListener("deviceorientation", orientationHandler);

    const detect = async () => {
      if (!running) return;

      const now = performance.now();
      if (now - lastDetectRef.current < THROTTLE_MS) {
        rafRef.current = requestAnimationFrame(detect);
        return;
      }
      lastDetectRef.current = now;

      if (video.readyState < 2) {
        if (loopCount % 20 === 0) {
          console.log("[FaceDetect] Video not ready, readyState:", video.readyState);
        }
        loopCount++;
        rafRef.current = requestAnimationFrame(detect);
        return;
      }

      try {
        const detection = await engine.detect(video);

        // MediaPipe's VIDEO mode rejects a repeated frame timestamp. The
        // loop is throttled well below camera framerate, so the same frame
        // can come round twice; skip without touching gate state.
        if (detection.repeatFrame) {
          loopCount++;
          rafRef.current = requestAnimationFrame(detect);
          return;
        }

        if (!firstInferenceDone) {
          firstInferenceDone = true;
          setDetectionStalled(false);
        }
        // First completed inference of the session — measures WebGL/shader
        // init + first-run cost, the exact target of detector warm-up work
        trackEvent("first_detection_completed", {
          inference_ms: Math.round(performance.now() - now),
          faces: detection.count,
        }, { once: true });
        if (detection.count >= 1) {
          trackEvent("face_first_detected", {}, { once: true });
        }

        const inferenceMs = Math.round(performance.now() - now);
        const frameH = video.videoHeight;
        const frameW = video.videoWidth;
        const newGating = { ...defaultGatingState };

        // Check overall frame brightness for dark room detection
        const frameBrightness = analyzeFrameBrightness(video);
        const tooDark = frameBrightness < DARK_ROOM_THRESHOLD;
        const sharpness = measureSharpness(video);
        // null (measurement failed) must never block capture
        const sharpEnough = sharpness == null || sharpness >= SHARPNESS_MIN;

        if (loopCount % 10 === 0) {
          console.log("[FaceDetect] Frame:", frameW, "x", frameH, "| Faces found:", detection.count, "| Brightness:", frameBrightness.toFixed(1));
        }

        let frameMetrics = {
          faces: detection.count,
          inference_ms: inferenceMs,
          brightness: Math.round(frameBrightness),
          sharpness,
          analysed_at: Math.round(now),
        };

        // Gate 1: exactly 1 face
        if (detection.count === 1) {
          newGating.facePresent = true;
          const box = detection.box;

          // Gate 2: face size (25-60% of frame height)
          const sizeRatio = box.height / frameH;
          newGating.faceSize =
            sizeRatio >= FACE_SIZE_MIN && sizeRatio <= FACE_SIZE_MAX;

          // Gate 3: centering (face center within 10% of frame center)
          const faceCenterX = box.x + box.width / 2;
          const faceCenterY = box.y + box.height / 2;
          const frameCenterX = frameW / 2;
          const frameCenterY = frameH / 2;
          const xOff = Math.abs(faceCenterX - frameCenterX) / frameW;
          const yOff = Math.abs(faceCenterY - frameCenterY) / frameH;
          newGating.centering =
            xOff <= CENTER_THRESHOLD && yOff <= CENTER_THRESHOLD;

          // Gate 4: pose. An engine can report a face it cannot pose
          // (BlazeFace occasionally returns a detection short of its
          // full keypoint set); treat that as the gate not passing
          // rather than failing the frame outright.
          // Engines may tighten the global floors (engine.limits); the
          // MediaPipe arm does, calibrated from its own capture outcomes.
          const limits = engine.limits || {};
          const yawLimit = limits.yawLimit ?? YAW_LIMIT;
          const lumaMin = limits.lumaMin ?? LUMA_MEAN_MIN;
          const { yaw, pitch, roll, pitchRatio } = detection.pose || {};
          const pitchRatioOk =
            limits.pitchRatioMax == null ||
            pitchRatio == null ||
            pitchRatio <= limits.pitchRatioMax;
          newGating.pose =
            detection.pose != null &&
            yaw < yawLimit &&
            pitch < PITCH_LIMIT &&
            roll < ROLL_LIMIT &&
            pitchRatioOk;

          // Gate 5: lighting
          const { mean, stdDev } = analyzeLighting(video, box);
          newGating.lighting =
            mean >= lumaMin &&
            mean <= LUMA_MEAN_MAX &&
            stdDev >= LUMA_STD_MIN;

          frameMetrics = {
            ...frameMetrics,
            size_ratio: Number(sizeRatio.toFixed(3)),
            x_off: Number(xOff.toFixed(3)),
            y_off: Number(yOff.toFixed(3)),
            yaw: yaw != null ? Number(yaw.toFixed(1)) : null,
            pitch: pitch != null ? Number(pitch.toFixed(1)) : null,
            roll: roll != null ? Number(roll.toFixed(1)) : null,
            pitch_ratio: detection.pose?.pitchRatio != null ? Number(detection.pose.pitchRatio.toFixed(3)) : null,
            luma_mean: Math.round(mean),
            luma_std: Number(stdDev.toFixed(1)),
          };

          if (loopCount % 10 === 0) {
            console.log("[FaceDetect] Gates:", {
              facePresent: newGating.facePresent,
              faceSize: `${newGating.faceSize} (ratio: ${sizeRatio.toFixed(3)}, need ${FACE_SIZE_MIN}-${FACE_SIZE_MAX})`,
              centering: `${newGating.centering} (xOff: ${xOff.toFixed(3)}, yOff: ${yOff.toFixed(3)}, limit: ${CENTER_THRESHOLD})`,
              pose: detection.pose
                ? `${newGating.pose} (yaw: ${yaw.toFixed(1)}/${yawLimit}, pitch: ${pitch.toFixed(1)}/${PITCH_LIMIT}, ratio: ${pitchRatio?.toFixed(3)}/${limits.pitchRatioMax ?? "-"}, roll: ${roll.toFixed(1)}/${ROLL_LIMIT})`
                : `${newGating.pose} (no keypoints)`,
              lighting: `${newGating.lighting} (mean: ${mean.toFixed(1)}/${lumaMin}, stdDev: ${stdDev.toFixed(1)})`,
            });
          }
        }

        // Record frame chain entry for capture integrity
        frameChainRef.current.push({
          t: Math.round(now),
          d: detection.count === 1 ? 1 : 0,
          g: Object.values(newGating).filter(Boolean).length,
        });
        if (frameChainRef.current.length > 200) frameChainRef.current.shift();

        const gatesPassing = Object.values(newGating).every(Boolean);
        // Sharpness sits outside gatingState on purpose: it is a shutter
        // condition, not a checklist item. Counting how often it alone
        // held an otherwise-ready frame measures the gate's cost.
        // Measured against the TARGET floors, not the active ones, so the
        // count stays meaningful while the floors are disabled.
        const wouldHold =
          (sharpness != null && sharpness < SHARPNESS_TARGET) ||
          (frameMetrics.luma_mean != null && frameMetrics.luma_mean < LUMA_TARGET);
        if (gatesPassing && wouldHold) blurHoldsRef.current += 1;
        const allPassing = gatesPassing && sharpEnough;
        lastFrameRef.current = {
          ...frameMetrics,
          gates_passing: Object.values(newGating).filter(Boolean).length,
          blur_holds: blurHoldsRef.current,
        };
        // Consecutive all-passing frames feeding the hold timer. With a
        // slow engine the 1800ms hold may rest on only a handful of
        // samples; this says how many.
        holdSamplesRef.current = allPassing ? holdSamplesRef.current + 1 : 0;

        setGatingState(newGating);
        setIsTooDark(tooDark);
        setFeedbackKey(getFeedbackKey(newGating, tooDark));
        setIsAllPassing(allPassing);

        // Auto-capture state machine
        if (allPassing) {
          // Every frame here has passed all five gates, so it is a valid
          // capture; remember the best one for the shutter to use.
          {
            const b = bestFrameRef.current;
            const s = scoreFrame(sharpness, frameMetrics.luma_mean);
            b.considered += 1;
            if (s > b.score) {
              try {
                if (!b.canvas) b.canvas = document.createElement("canvas");
                if (b.canvas.width !== frameW) b.canvas.width = frameW;
                if (b.canvas.height !== frameH) b.canvas.height = frameH;
                b.canvas.getContext("2d").drawImage(video, 0, 0, frameW, frameH);
                b.score = s;
                b.sharpness = sharpness;
                b.luma = frameMetrics.luma_mean ?? null;
                b.at = performance.now();
              } catch (e) {
                // A failed copy leaves the previous best in place.
              }
            }
          }
          if (!allPassingStartRef.current) {
            allPassingStartRef.current = performance.now();
            trackEvent("gates_first_passed", {}, { once: true });
            console.log("[FaceDetect] All gates PASSING — starting 1800ms timer");
          } else {
            const elapsed = performance.now() - allPassingStartRef.current;
            if (elapsed >= AUTO_CAPTURE_DELAY_MS) {
              console.log("[FaceDetect] Auto-capture triggered! Elapsed:", elapsed.toFixed(0), "ms, callback exists:", !!onAutoCaptureRef.current);
              trackEvent("auto_capture_triggered", { hold_ms: AUTO_CAPTURE_DELAY_MS });
              if (onAutoCaptureRef.current) {
                running = false;
                onAutoCaptureRef.current();
                return;
              }
            }
          }
        } else {
          if (allPassingStartRef.current) {
            console.log("[FaceDetect] Gates broke — timer reset");
          }
          allPassingStartRef.current = null;
          resetBestFrame();
        }
      } catch (err) {
        console.error("[FaceDetect] Detection error:", err);
      }

      loopCount++;
      if (running) {
        rafRef.current = requestAnimationFrame(detect);
      }
    };

    rafRef.current = requestAnimationFrame(detect);

    return () => {
      running = false;
      clearTimeout(stallTimer);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener("deviceorientation", orientationHandler);
    };
  }, [modelsLoaded, active, videoRef]);

  const getOrientationSamples = useCallback(() => [...orientationRef.current], []);

  return {
    modelsLoaded,
    modelLoadError,
    detectionStalled,
    gatingState,
    feedbackKey,
    isAllPassing,
    isTooDark,
    setOnAutoCapture,
    getFrameChain,
    getLastFrameMetrics,
    getBestFrame,
    getOrientationSamples,
  };
}
